import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { googleSync } from '../api';
import { Logo, Spinner } from '../components/UI';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// Supabase redirects here after Google OAuth.
// We wait for the session to be established, call google-sync to ensure the
// user exists in our DB, then send them to set up their username or home.
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    // Check for OAuth error params first
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    const errorMsg = params.get('error_description') || hashParams.get('error_description');

    if (errorMsg) {
      setError(errorMsg);
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    // Wait for Supabase to establish the session from the OAuth callback URL,
    // then call our backend to sync the user row and decide where to send them.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
          try {
            const result = await googleSync(session.access_token);
            // needs_username is true when we just created the row (new user)
            if (result.needs_username) {
              navigate('/setup-username', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          } catch (_) {
            // Sync failed — still let them in, they can set username later
            navigate('/', { replace: true });
          }
        }
      }
    );

    // Safety-net timeout in case onAuthStateChange never fires
    // (e.g. user lands here directly without a valid OAuth callback)
    const fallback = setTimeout(() => navigate('/login', { replace: true }), 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallback);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-green-100 rounded-full opacity-40" />
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-amber-100 rounded-full opacity-50" />
      </div>

      <div className="w-full max-w-sm relative text-center">
        <Logo size="lg" className="mx-auto mb-4" />
        {error ? (
          <>
            <p className="text-red-600 font-medium mb-2">Sign-in failed</p>
            <p className="text-sm text-gray-500">{error}</p>
            <p className="text-xs text-gray-400 mt-2">Redirecting you back...</p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Spinner size="md" />
            <p className="text-gray-600 font-medium">Signing you in...</p>
          </div>
        )}
      </div>
    </div>
  );
}